// src/components/Pages/Blog.jsx

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../Section';
import BlogSectionStyle2 from '../Section/BlogSection/BlogSectionStyle2';
import Breadcrumb from '../Breadcrumb';
import { pageTitle } from '../../helpers/PageTitle';
import { getBlogArticles } from '../../helpers/GetBlogArticle';
import Pagination from '../Pagination';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import languages from '../../config/languages';

const localeMap = {
  es: 'es-ES',
  en: 'en-US',
  fr: 'fr-FR',
  it: 'it-IT',
  de: 'de-DE',
};

// Idiomas soportados
const prefixedLanguages = Object.keys(languages);

export default function Blog() {
  const { t, i18n, ready } = useTranslation();
  const location = useLocation();
  const { page } = useParams(); // <-- 'page' proviene de la ruta '/blog/page/:page'

  // Detectar idioma según la URL
  const pathSegments = location.pathname.split('/').filter(Boolean);
  // El primer segmento podría ser "en", "fr", etc.
  const language = pathSegments.length && prefixedLanguages.includes(pathSegments[0])
    ? pathSegments[0]
    : 'es';

  // Cambiar idioma si hace falta
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // Establecer título
  pageTitle(t('blog_title'));

  // Convertir `page` a número; si no existe, usar 1
  const initialPage = page ? parseInt(page, 10) : 1;

  // Estados
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Actualizar estado si cambia el parámetro :page
  useEffect(() => {
    setCurrentPage(page ? parseInt(page, 10) : 1);
  }, [page]);

  // Cargar artículos
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setIsLoading(true);
        const data = await getBlogArticles(currentPage, language);
        setArticles(data.data);
        setTotalPages(data.last_page);
        setIsLoading(false);

        // Soporte react-snap
        if (window.snapSaveState) {
          window.snapSaveState();
        }
      } catch (error) {
        console.error('Error fetching blog articles', error);
        setIsLoading(false);
      }
    };
    fetchArticles();
  }, [currentPage, language]);

  // Procesar data para BlogSectionStyle2
  const blogData = articles.map((article) => {
    const chosenLocale = localeMap[language] || 'en-US';
    return {
      title: article.title,
      thumbUrl: article.image.startsWith('http')
        ? article.image
        : `https://dashboard.gestlifesurrogacy.com/${article.image}`,
      date: new Date(article.date).toLocaleDateString(chosenLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      btnText: t('learn_more'),
      // Detalle con barra final
      href: `/${language !== 'es' ? `${language}/` : ''}blog/${article.slug}/`,
      socialShare: true,
    };
  });

  // Title y description
  const pageTitleText = currentPage > 1
    ? `${t('blog_title')} - ${t('page')} ${currentPage}`
    : t('blog_title');

  const metaDescription = currentPage > 1
    ? `${t('meta_description_blog')} ${t('page')} ${currentPage}`
    : t('meta_description_blog');

  // Generar URL canónica con /page/X
  // (Aquí sigues generando con "page-${pageNum}" si lo deseas; 
  //  lo recomendable es hacerlo coincidir con /page/${pageNum} también)
  const generateUrl = (pageNum) => {
    const prefix = language !== 'es' ? `${language}/` : '';
    return pageNum > 1
      ? `https://www.gestlifesurrogacy.com/${prefix}blog/page/${pageNum}/`
      : `https://www.gestlifesurrogacy.com/${prefix}blog/`;
  };

  if (!ready) return null;

  return (
    <>
      <Helmet>
        <title>{pageTitleText}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={generateUrl(currentPage)} />
        {currentPage > 1 && <link rel="prev" href={generateUrl(currentPage - 1)} />}
        {currentPage < totalPages && <link rel="next" href={generateUrl(currentPage + 1)} />}
      </Helmet>

      <Section
        style={{
          background: 'linear-gradient(40deg, #EE6FBB, #7BADD3)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '50px ',
          minHeight: '150px', // Add this line to ensure minimum height
        }}
      />

      <Section topMd={70} bottomMd={96} bottomLg={70}>
        <Breadcrumb title={pageTitleText} />
      </Section>

      <Section bottomMd={170} bottomLg={150} bottomXl={110}>
        {isLoading ? (
          <p>{t('loading_articles')}</p>
        ) : (
          <BlogSectionStyle2 data={blogData} />
        )}
      </Section>

      <Section bottomMd={30} bottomLg={30} bottomXl={30}>
        <Pagination currentPage={currentPage} totalPages={totalPages} language={language} />
      </Section>
    </>
  );
}
